import { RefObject, useCallback, useEffect, useRef } from "react";
import { UseFormReturn } from "react-hook-form";
import { useDrugDetails } from "./useDrugDetails";
import { formatNdc, clampNdcInputElevenDigits, isNdcReadyForDrugLookup } from "../utilities/ndc/ndcUtils";
import { Product } from "../network/models/ProductInterface";
import { ReturnItemFields } from "../network/models/FormInterfaces";

export const DRUG_LOOKUP_DEBOUNCE_MS = 450;

type UseDebouncedDrugLookupParams = {
  itemForm: UseFormReturn<ReturnItemFields>;
  quantityRef?: RefObject<HTMLInputElement>;
  debounceMs?: number;
};

export function useDebouncedDrugLookup({
  itemForm,
  quantityRef,
  debounceMs = DRUG_LOOKUP_DEBOUNCE_MS,
}: UseDebouncedDrugLookupParams) {
  const isUpdatingNdcRef = useRef(false);
  /** Normalized NDC (formatNdc) used for dedup and submit; matches useAddRequestItem formatNdc path. */
  const rawNdcRef = useRef("");
  const drugLookupDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const productDataRef = useRef<Product | undefined>();

  const cancelDrugLookupDebounce = useCallback(() => {
    if (drugLookupDebounceRef.current) {
      clearTimeout(drugLookupDebounceRef.current);
      drugLookupDebounceRef.current = null;
    }
  }, []);

  const clearProductFields = useCallback(() => {
    itemForm.setValue("manufacturer", "");
    itemForm.setValue("pkgSize", undefined);
    itemForm.setValue("controlledSubstanceCode", "");
    itemForm.setValue("description", "");
    itemForm.setValue("strength", "");
    itemForm.setValue("dosage", "");
  }, [itemForm]);

  const applyProductToForm = useCallback(
    (data: Product) => {
      itemForm.setValue("manufacturer", data.manufacturer ?? "");
      itemForm.setValue("pkgSize", data.package_size ?? undefined);
      itemForm.setValue(
        "controlledSubstanceCode",
        data.Controlled_Substance_Code ?? ""
      );
      itemForm.setValue("description", data.item_name ?? "");
      itemForm.setValue("strength", data.strength ?? "");
      itemForm.setValue("dosage", data.dosage ?? "");
      itemForm.setValue("ndc", clampNdcInputElevenDigits(data.product_ndc ?? ""));
    },
    [itemForm]
  );

  const [productData, setProductData] = useDrugDetails({
    onSuccess: (data) => {
      if (!data) return;

      productDataRef.current = data;
      cancelDrugLookupDebounce();
      isUpdatingNdcRef.current = true;
      rawNdcRef.current = formatNdc(data.product_ndc);
      applyProductToForm(data);

      setTimeout(() => {
        isUpdatingNdcRef.current = false;
        quantityRef?.current?.focus();
      }, 0);
    },
  });

  useEffect(() => {
    productDataRef.current = productData;
  }, [productData]);

  const resetDrugLookupState = useCallback(() => {
    cancelDrugLookupDebounce();
    rawNdcRef.current = "";
    productDataRef.current = undefined;
    setProductData("");
  }, [cancelDrugLookupDebounce, setProductData]);

  const scheduleDrugLookup = useCallback(
    (ndcValue: string, immediate = false) => {
      const formatted = formatNdc(ndcValue);
      if (!isNdcReadyForDrugLookup(formatted)) return;

      if (formatted === rawNdcRef.current) return;

      const runLookup = () => {
        if (isUpdatingNdcRef.current) return;

        const currentNdc = itemForm.getValues("ndc");
        const currentFormatted = currentNdc ? formatNdc(currentNdc) : "";
        if (!currentFormatted || !isNdcReadyForDrugLookup(currentFormatted)) return;
        if (currentFormatted !== formatted) return;
        if (currentFormatted === rawNdcRef.current) return;

        const loadedFormatted = productDataRef.current?.product_ndc
          ? formatNdc(productDataRef.current.product_ndc)
          : "";
        if (currentFormatted !== loadedFormatted) {
          clearProductFields();
        }

        rawNdcRef.current = currentFormatted;
        setProductData(currentFormatted);
      };

      cancelDrugLookupDebounce();
      if (immediate) {
        runLookup();
      } else {
        drugLookupDebounceRef.current = setTimeout(runLookup, debounceMs);
      }
    },
    [
      itemForm,
      clearProductFields,
      cancelDrugLookupDebounce,
      setProductData,
      debounceMs,
    ]
  );

  useEffect(() => () => cancelDrugLookupDebounce(), [cancelDrugLookupDebounce]);

  return {
    productData,
    scheduleDrugLookup,
    resetDrugLookupState,
    cancelDrugLookupDebounce,
    rawNdcRef,
    isUpdatingNdcRef,
  };
}
